import Bowser from 'bowser';

// Mapping from bowser names to browserslist names
const BROWSER_MAP: Record<string, string> = {
  Chrome: 'chrome',
  Firefox: 'firefox',
  Safari: 'safari',
  'Microsoft Edge': 'edge',
  Opera: 'opera',
  'Samsung Internet for Android': 'samsung',
  'Android Browser': 'android',
  'Opera Mini': 'op_mini',
  'UC Browser': 'and_uc',
  'QQ Browser': 'and_qq',
};

export interface BrowserSupport {
  supported: boolean;
  /** As Bowser names it, e.g. "Chrome". */
  name: string;
  version: string;
}

/**
 * Whether the browser behind `userAgent` meets the minimum versions from the
 * browserslist query (`VITE_MIN_BROWSERS`, computed in vite.config.ts). With no
 * minimums, every browser is allowed.
 */
export function checkBrowserSupport(
  userAgent: string,
  minBrowsers: Record<string, number> | undefined
): BrowserSupport {
  const browser = Bowser.getParser(userAgent);
  const name = browser.getBrowserName();
  const version = browser.getBrowserVersion() || '';
  if (!minBrowsers || Object.keys(minBrowsers).length === 0) {
    return { supported: true, name, version };
  }

  const osName = browser.getOSName();
  const platformType = browser.getPlatformType();
  const majorVersion = parseInt(version.split('.')[0], 10);

  // Chrome on Android, and the Android WebView, which Bowser also reports as Chrome, stay on
  // `chrome`. caniuse tracks only the newest Chrome for Android, so browserslist's `and_chr`
  // minimum is always the latest release, which would turn away anyone a release behind,
  // such as the WebView in the Foresight mobile app. Chrome uses the same version numbers on
  // Android and desktop, so the desktop minimum applies.
  let configKey = BROWSER_MAP[name];

  // Special handling for iOS Safari
  if (name === 'Safari' && (osName === 'iOS' || platformType === 'mobile' || platformType === 'tablet')) {
    configKey = 'ios_saf';
  }

  // Special handling for Firefox on Android
  if (name === 'Firefox' && osName === 'Android') {
    if (minBrowsers['and_ff']) {
      configKey = 'and_ff';
    }
  }

  // Special handling for Opera on Mobile
  if (name === 'Opera' && (platformType === 'mobile' || platformType === 'tablet')) {
    if (minBrowsers['op_mob']) {
      configKey = 'op_mob';
    }
  }

  // Strict check: Must be in the map AND meet version requirement
  // If the browser is recognized (configKey exists) check against minBrowsers
  if (configKey && typeof minBrowsers[configKey] !== 'undefined') {
    const minVersion = minBrowsers[configKey];
    // minVersion 0 means "all" or no strict version limit
    return { supported: !(minVersion > 0 && majorVersion < minVersion), name, version };
  }
  // Browser not in our supported list or not recognized
  // If configKey is defined but not in minBrowsers, it means browserslist didn't include it
  // which implies it's not supported by the query (e.g. it is "dead" or < 1%)
  return { supported: false, name, version };
}
